import {
    createAction,
    createAsyncThunk,
    createSlice,
    current,
    PayloadAction,
} from "@reduxjs/toolkit";
import { instance, pathname } from "./ActionCreators";
import axios from "axios";
import notifySuccess from "utils/Notifications/Success";
import { RootState, store } from "../store";
import notifyError from "../../utils/Notifications/Error";
import { setActivePageName } from "./QuotesPageReducer";
import tablesSortWrapper from "../../utils/sortWorkspaces";
import {
    setFilteredColumns,
    setGrid,
    setOrder,
    setQuotes,
    setQuotesInfo,
} from "./quotes";
import { deepEqual } from "fast-equals";
import TickerParamsHandler from "utils/ticker-params-handler";
import { initColorsFilter, selectFilterParam } from "./FilterSlice";
import {
    markCommandCompleted,
    removeCompletedCommand,
    removePendingCommand,
    setMassCommandPending,
    setPendingCommand,
    setUiBlock,
} from "./LogsSlice";
import { showModal } from "./modal";

function updateForWorkspacesHasDiff(update: any[], state: any[]) {
    if (!Array.isArray(update) || !Array.isArray(state)) {
        return false;
    }
    for (let x = 0; x < update.length; x++) {
        const observable = update[x];
        const from_state = state[x];
        const check = deepEqual(observable, from_state);
        if (!check) {
            return true;
        }
    }
    return false;
}

export const stylesObject = {
    optionStyles: {
        flexDirection: "row",
        flexWrap: "nowrap",
    },
    viewStyles: {
        tabWidth: "100%",
        otherTabWidth: "100%",
        otherTabRequired: false,
    },
};

export type IGrid = "1-0" | "2-0" | "1-1" | "3-0" | "1-2" | "4-0" | "2-2";

interface IFilter {
    query: string;
    names?: string[]; // A
    virt?: boolean; // B
    stopped?: boolean; // C
    close?: boolean; // D
    mm?: boolean; // E
    no_open?: boolean; // F
}

export interface TcpQueueItem {
    tcp_queue_id: number;
    ticker_name: string;
}

export interface ISectionCommandResponse {
    tcp_queues?: TcpQueueItem[];
}

export interface ISection {
    id: number;
    filter: IFilter;
    columns: string;
    width: number;
    height: number;
    rowHeight?: number;
    font_scale?: number;
    row_density: "low" | "medium" | "high";
    statuses: string[];
    name: string | null;
    columns_widths: any;
}

export interface ICurrentTab {
    id: number;
    name: string;
    order: Array<number>;
    grid: IGrid;
    sections: ISection[];
}
export interface TickerStatus {
    ticker_name: string;
    status: "OK" | "V" | "W" | "R" | "E";
}

export interface ITableSizeSetting {
    id: number;

    fontScale?: "small" | "medium" | "large";
    row_density: "low" | "medium" | "high";
    isAutoSizing: boolean;
}

type TabsState = {
    loading: boolean;
    error: string | null;
    tabs: Array<any>; // вкладки
    workspaces: Array<any>; // рабочие области
    workspaceToUpdateId: number | null; // id пустой рабочей области, в которую мы добавляем тикеры в модалке
    tickers: Array<string>; // тикеры, например AA~DEP~SPB
    addTickerPopup: Array<string>; // тикеры без инструментов, например AA
    availableTickers: Array<string>; // тикеры которые мы показываем в модалке выбора тикеров
    currentTab: ICurrentTab; // текущая вкладка
    pickedTickers: Array<any>; // выбранные тикеры из модалки создания рабочей области
    tabsNumber: number;
    optionId: "1-1" | "2-1" | "2-2" | "3-1" | "3-2" | "4-1" | "4-2";
    initialColumns: string[]; // нефильтрованные столбцы параметров
    workspaceColumns: string[]; // columns конкретной рабочей области
    initial_workspace_fetch_done?: boolean;
    tickerStatuses: { [ticker_name: string]: TickerStatus };
    sectionStatuses: Record<number, boolean>;
    sectionsNameColumnPinStatus: Record<number, boolean>;
    currentGridPage: number;
    srt_schedule: Array<{ ticker_name: string; scheduled_for: string | null }>;
    tables_sizes: ITableSizeSetting[];
    pendingTickerCommands: Record<string, boolean>;
    errorUpdateTickers: string | null;
};

const initialState: TabsState = {
    loading: false,
    error: null,
    tabs: [],
    workspaces: [],
    workspaceToUpdateId: null,
    tickers: [],
    addTickerPopup: [],
    availableTickers: [],
    currentTab: {} as ICurrentTab,
    pickedTickers: [],
    tabsNumber: 1,
    optionId: "1-1",
    initialColumns: [],
    workspaceColumns: [],
    tickerStatuses: {},
    sectionStatuses: {},
    currentGridPage: 0,
    srt_schedule: [],
    tables_sizes: [],
    sectionsNameColumnPinStatus: {},
    pendingTickerCommands: {},
    errorUpdateTickers: null,
};

export const updateAreaPickedTickers = createAsyncThunk(
    "patch/workspace",
    async (
        {
            workspace_id,
            area_id,
            picked_tickers,
        }: {
            workspace_id: string | number;
            area_id: string | number;
            picked_tickers: string[];
        },
        { dispatch, rejectWithValue },
    ) => {
        let request;

        const body = {
            area: workspace_id,
            filter: {
                names: picked_tickers,
            },
        };
        try {
            request = await instance.patch(
                pathname + `/api/workspace_config/${area_id}`,
                body,
            );
            notifySuccess(`Список тикеров обновлен`);
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
        return request.data;
    },
);

export const getTabs = createAsyncThunk(
    "get/tabs",
    async (_, { rejectWithValue }) => {
        try {
            const response = await instance.get(pathname + "/api/tab");

            return response.data;
        } catch (e) {
            if (axios.isAxiosError(e)) {
                console.error(e.message);
            }
        }
    },
);
export const setActiveTab = createAction<number>("tabs/setActiveTab");
export const getTabById = createAsyncThunk(
    "get/tabs/id",
    async (id: number, { rejectWithValue, dispatch, getState }) => {
        try {
            const state = getState();
            //@ts-ignore
            // ?? ??
            // const active_page_id = state.quotes.active_page_id;
            // if (active_page_id !== id) {
            //     return 'active_page_id !== id';
            // }
            //@ts-ignore
            if (!state.auth.loggedIn) {
                return;
            }
            const response = await instance.get(pathname + `/api/tab/${id}`);
            const sections = response.data?.sections || [];

            sections.forEach((section: any) => {
                const colorFilter =
                    section.colors_filter || section.color_filter || [];

                let fontSize = 14;

                if (typeof section.font_scale === "number") {
                    fontSize =
                        section.font_scale === 0
                            ? 10
                            : section.font_scale === 1
                              ? 14
                              : 18;
                } else if (typeof section.font_size === "number") {
                    fontSize = section.font_size;
                }
                dispatch(
                    initColorsFilter({
                        id: section.id,
                        colors: colorFilter,
                    }),
                );
                let fontScale: "small" | "medium" | "large" = "medium";

                if (section.font_scale === "small") fontScale = "small";
                else if (section.font_scale === "large") fontScale = "large";
                else if (typeof section.font_scale === "number") {
                    fontScale =
                        section.font_scale === 0
                            ? "small"
                            : section.font_scale === 2
                              ? "large"
                              : "medium";
                }

                dispatch(
                    setTableSettings({
                        id: section.id,
                        row_density: section.row_density || "medium",
                        fontScale,
                        isAutoSizing: section.auto_width ?? false,
                    }),
                );
            });
            // const sorted = {...response.data, sections: tablesSortWrapper(response.data.sections, response.data.order)}
            dispatch(getAreasByTabId({ id, order: response.data.order }));

            if (typeof response.data === "object") {
                if (Array.isArray(response.data.order)) {
                    dispatch(setOrder(response.data.order));
                }

                if (typeof response.data.grid === "string") {
                    dispatch(setGrid(response.data.grid));

                    switch (response.data.grid) {
                        case "1-0":
                            dispatch(setGridPage(0));
                            break;
                        case "2-0":
                        case "1-1":
                            dispatch(setGridPage(1));
                            break;
                        case "1-2":
                        case "3-0":
                            dispatch(setGridPage(2));
                            break;
                        case "2-2":
                        case "4-0":
                            dispatch(setGridPage(3));
                            break;
                        default:
                            dispatch(setGridPage(0));
                    }
                }

                if (Array.isArray(response.data.sections)) {
                    dispatch(
                        setQuotesInfo({
                            tab_id: response.data.id,
                            workspaces: response.data.sections.map((s) => s.id),
                            statuses: response.data,
                        }),
                    );
                }

                if (Array.isArray(response.data.sections)) {
                    dispatch(
                        initSectionStatusesWithValues(
                            response.data.sections.map((section) => {
                                return {
                                    id: section.id,
                                    value: section.is_collapsed,
                                };
                            }),
                        ),
                    );

                    response.data.sections.map((section) => {
                        dispatch(
                            changeSectionsNameColumnPinStatus({
                                id: section.id,
                                value: section.is_name_pinned,
                            }),
                        );
                    });

                    for (const workspace of response.data.sections) {
                        if (
                            workspace &&
                            workspace.filter &&
                            typeof workspace.filter?.query === "string" &&
                            workspace.filter.query?.length
                        ) {
                            const formulas = TickerParamsHandler.parseFormula(
                                workspace.filter?.query,
                            );
                            formulas.forEach((param) => {
                                dispatch(
                                    selectFilterParam({
                                        id: workspace.id,
                                        param,
                                    }),
                                );
                            });
                        }
                    }
                }
            }
            return response.data;
        } catch (e) {
            if (axios.isAxiosError(e)) {
                return rejectWithValue(e.message);
            }
        }
    },
);
export const updateTableSizes = createAsyncThunk(
    "update/tableSizes",
    async (
        {
            config_id,
            rowDensity,
            fontScale,
            autoWidth = false,
            forAll = false,
        }: {
            config_id: number;
            rowDensity: "low" | "medium" | "high";
            fontScale: "small" | "medium" | "large";
            autoWidth?: boolean;
            forAll?: boolean;
        },
        { dispatch, getState, rejectWithValue },
    ) => {
        try {
            const body: any = {
                row_density: rowDensity,
                font_scale: fontScale,
                auto_width: autoWidth,
            };

            if (forAll) {
                body.for_all = true;
            }

            const response = await instance.patch(
                `${pathname}/api/workspace_config/${config_id}`,
                body,
            );

            const tabId = (getState() as any).tabs.currentTab?.id;
            if (tabId) {
                dispatch(getTabById(tabId));
            }

            notifySuccess(
                forAll
                    ? "Настройки применены ко всем таблицам"
                    : "Настройки таблицы сохранены",
            );

            return response.data;
        } catch (e: any) {
            notifyError("Ошибка при сохранении настроек размера таблицы");
            return rejectWithValue(e.response?.data || e.message);
        }
    },
);
export const getAreasByTabId = createAsyncThunk(
    "get/areas",
    async (
        { id, order }: { id: number; order: Array<number> },
        { rejectWithValue, getState, dispatch },
    ) => {
        try {
            const response = await instance.get(
                pathname + `/api/workspaces/${id}`,
            );

            for (let i = 0; i < response.data.length; i++) {
                dispatch(
                    setFilteredColumns({
                        id: response.data[i].id,
                        columns: response.data[i].columns,
                        all_columns: response.data[i].all_columns,
                    }),
                );
            }

            dispatch(setQuotes({ data: response.data, reference: id }));
            return tablesSortWrapper(response.data, order);
        } catch (e) {
            if (axios.isAxiosError(e)) {
                notifyError(e.message);
                return rejectWithValue(e.message);
            }
        }
    },
);

export const addNewWorkspace = createAsyncThunk(
    "add/workspace",
    // @ts-ignore
    async (_, { rejectWithValue, getState }) => {
        const { tabs } = getState() as any;
        const { currentTab, pickedTickers } = tabs;
        try {
            const request = {
                area: currentTab.id,
                filter: {
                    query: "A", // по сути когда мы создаем раб. область нам интересны только тикеры, которые мы отправляем в names
                    // но возможно сюда нужно будет вернуться
                    pickedTickers, // A
                },
                columns: "string",
            };
            const response = await instance.post(
                pathname + "/api/workspace_config",
                request,
            );
            notifySuccess("Вы создали рабочую область");
            return response.data;
        } catch (e) {
            if (axios.isAxiosError(e)) {
                notifyError("Ошибка при создании рабочей области");
                return rejectWithValue(e.message);
            }
        }
    },
);

export const updateWorkspace = createAsyncThunk(
    "update/workspace",
    async (_, { rejectWithValue, getState }) => {
        const state = getState() as RootState;
        try {
            //@ts-ignore
            const page = state.quotesPage?.activePage;
            //@ts-ignore
            const {
                workspaceToUpdateId: id,
                currentTab,
                pickedTickers,
            } = state.tabs;
            const names = ["nothing"];

            const { filter } = currentTab.sections.find(
                (section: ISection) => section.id === id,
            );
            const body = {
                area: page.id,
                filter: {
                    ...filter,
                    query: "A",
                    names: [...names, ...pickedTickers],
                },
            };
            const response = await instance.patch(
                pathname + `/api/workspace_config/${id}`,
                body,
            );
            notifySuccess(`Список тикеров обновлен`);
            return response.data;
        } catch (e) {
            console.log(e);
            if (axios.isAxiosError(e)) {
                notifyError("Ошибка при обновлении рабочей области");
                return rejectWithValue(e.message);
            }
        }
    },
);

export const updateWorkspaceWidths = createAsyncThunk<
    any,
    { id: number; columns_widths: { [key: string]: string } },
    { state: any }
>(
    "update/workspaceWidths",
    async ({ id, columns_widths }, { rejectWithValue, dispatch, getState }) => {
        const state = getState() as any;
        const { id: tabId } = state.tabs.currentTab;
        try {
            const body = {
                columns_widths,
            };
            const response = await instance.patch(
                pathname + `/api/workspace_config/${id}`,
                body,
            );
            dispatch(getTabById(tabId));
            return response.data;
        } catch (e) {
            if (axios.isAxiosError(e)) {
                notifyError("Ошибка при обновлении ширины столбцов");
                return rejectWithValue(e.message);
            }
        }
    },
);

export const updateWorkspaceColumns = createAsyncThunk<
    any,
    { id: number; columns: string | null },
    { state: any }
>(
    "update/workspaceColumns",
    async ({ id, columns }, { rejectWithValue, dispatch, getState }) => {
        const state = getState() as any;
        const { id: tabId, order } = state.tabs.currentTab;
        try {
            const body = {
                columns,
            };
            const response = await instance.patch(
                pathname + `/api/workspace_config/${id}`,
                body,
            );
            notifySuccess("Настройки отображения успешно изменены");
            dispatch(getAreasByTabId({ id: tabId, order }));
            return response.data;
        } catch (e) {
            if (axios.isAxiosError(e)) {
                notifyError("Ошибка при обновлении настроек отображения");
                return rejectWithValue(e.message);
            }
        }
    },
);

export const addTab = createAsyncThunk<
    void,
    { name: string },
    { state: RootState }
>("add/tab", async ({ name }, { rejectWithValue, dispatch, getState }) => {
    try {
        const { tabs } = getState().tabs;

        if (
            tabs.filter((it: { name: string }) => it.name === name).length === 0
        ) {
            const request = {
                name,
                order: null,
            };
            const response = await instance.post(
                pathname + `/api/tab`,
                request,
            );
            dispatch(getTabs());
            notifySuccess(`Вкладка ${name} создана`);
            return response.data;
        } else notifyError("Вкладка с таким названием уже существует");
    } catch (e) {
        if (axios.isAxiosError(e)) {
            notifyError("Ошибка при создании вкладки");
            return rejectWithValue(e.message);
        }
    }
});

export const removeTab = createAsyncThunk(
    "remove/tab",
    async (id: number, { rejectWithValue, dispatch }) => {
        try {
            const response = await instance.delete(pathname + `/api/tab/${id}`);
            dispatch(getTabs());
            return response.data;
        } catch (e) {
            if (axios.isAxiosError(e)) {
                notifyError("Ошибка при удалении вкладки");
                return rejectWithValue(e.message);
            }
        }
    },
);

export const renameTab = createAsyncThunk<
    any,
    { name: string; id: number },
    { state: RootState }
>(
    "rename/tabs",
    async ({ name, id }, { rejectWithValue, dispatch, getState }) => {
        try {
            const { tabs } = getState().tabs;

            if (
                tabs.filter((it: { name: string }) => it.name === name)
                    .length === 0
            ) {
                const request = {
                    name: name,
                };
                const response = await instance.patch(
                    pathname + `/api/tab/${id}`,
                    request,
                );
                dispatch(setActivePageName(name as string));
                dispatch(getTabs());
                notifySuccess("Название вкладки изменено");
                return response.data;
            } else {
                dispatch(getTabs());
                dispatch(
                    showModal({
                        modal_type: "are_you_sure",
                        value: false,
                    }),
                );
                notifyError("Вкладка с таким названием уже существует");
            }
        } catch (e) {
            if (axios.isAxiosError(e)) {
                dispatch(
                    showModal({
                        modal_type: "are_you_sure",
                        value: false,
                    }),
                );
                notifyError("Ошибка при переименовании вкладки");
                return rejectWithValue(e.message);
            }
        }
    },
);

export const getTickers = createAsyncThunk(
    "get/tickers",

    async (_, { rejectWithValue }) => {
        try {
            const body = {
                query: "",
            };
            const response = await instance.post(
                pathname + "/api/filter",
                body,
            );
            return response.data;
        } catch (e) {
            if (axios.isAxiosError(e)) {
                notifyError(e.message);
                return rejectWithValue(e.message);
            }
        }
    },
);

export const sendTickerCommand = createAsyncThunk(
    "post/tickerCommand",
    async (
        {
            ticker_name,
            command,
            additionalData,
        }: { ticker_name?: string; command: string; additionalData?: object },
        { rejectWithValue, dispatch, getState },
    ) => {
        try {
            const body = {
                ticker_name,
                command,
                ...additionalData,
            };
            const isMassCommand = !ticker_name;

            if (isMassCommand) {
                dispatch(setMassCommandPending(true));
            }
            const response = await instance.post<ISectionCommandResponse>(
                pathname + "/api/ticker/command",
                body,
            );
            const { logs } = getState() as RootState;
            if (ticker_name) {
                const tcpQueues = response.data.tcp_queues;

                const shouldSetTickerPending =
                    ticker_name &&
                    tcpQueues?.some((queue) => {
                        const { logs } = getState() as RootState;
                        return !logs.completedCommands[queue.tcp_queue_id];
                    });

                if (shouldSetTickerPending) {
                    dispatch(
                        setPendingTickerCommand({
                            ticker_name,
                            pending: true,
                        }),
                    );
                }
            }
            const tcpQueues = response.data.tcp_queues;

            if (tcpQueues && tcpQueues.length > 0) {
                tcpQueues.forEach((queue: any) => {
                    if (!logs.completedCommands[queue.tcp_queue_id]) {
                        dispatch(
                            setPendingCommand({
                                tcp_queue_id: queue.tcp_queue_id,
                                ticker_name: queue.ticker_name || ticker_name,
                                command_type: command,
                                is_mass: !ticker_name,
                            }),
                        );
                    } else {
                        dispatch(removeCompletedCommand(queue.tcp_queue_id));
                    }
                });
            }
            if (isMassCommand) {
                dispatch(setMassCommandPending(false));
            }
            let message = "";
            switch (command) {
                case "RRO":
                    message = `Команда "Перезагрузка с переподпиской всех тикеров" отправлена`;
                    break;
                case "RR":
                    message = `Команда "Перезагрузка без переподписки всех тикеров" отправлена`;
                    break;
                case "WTS":
                    message = `Команда "Перевод в бой всех тикеров" отправлена`;
                    break;
                case "VTS":
                    message = `Команда "Перевод в вирт всех тикеров" отправлена`;
                    break;
                case "SC":
                    message = `Команда "Перевод в закрытие всех тикеров" отправлена`;
                    break;
                case "SA":
                    message = `Команда "Выставлены АЗ для всех стратегий" отправлена`;
                    break;
                case "RA":
                    message = `Команда "Отменен счетчик АЗ всех тикеров" отправлена`;
                    break;
                case "SP":
                    message = `Команда "Получен путь к папке с конфигами" отправлена`;
                    break;
                case "WT":
                    message = `Команда "Перевод в бой" отправлена для тикера  ${ticker_name}`;
                    break;
                case "VT":
                    message = `Команда "Перевод в вирт" отправлена для тикера ${ticker_name}`;
                    break;
                case "SCT":
                    message = `Команда "Перевод в закрытие" отправлена для тикера ${ticker_name}`;
                    break;
                case "CCT":
                    message = `Команда "Отменить в закрытие" отправлена для тикера  ${ticker_name}`;
                    break;
                case "SRT":
                    message = `Команда "Перевод в Real режим" отправлена для тикера  ${ticker_name} `;
                    break;
                case "CRT":
                    message = `Команда "Отмена перевода в Real режим" отправлена для тикера  ${ticker_name} `;
                    break;
                case "SAT":
                    message = `Команда "Выставить в АЗ" отправлена для тикера ${ticker_name}`;
                    break;
                case "CC":
                    message = `Команда "Перевод в закрытие для всех тикеров отменен" отправлена`;
                    break;
                case "RT":
                    message = ticker_name
                        ? `Команда "Перезагрузка" отправлена для тикера ${ticker_name}`
                        : `Команда "Общая перезагрузка" отправлена`;

                    if (response.status === 200) {
                        // console.log("response", response);
                        notifySuccess(message);
                        dispatch(setUiBlock(null));
                    }
                    break;

                default:
                    message = `Команда ${command} отправлена для ${ticker_name} `;
            }
            notifySuccess(message);

            return response.data;
        } catch (e) {
            if (!ticker_name) {
                dispatch(setMassCommandPending(false));
            }
            if (ticker_name) {
                dispatch(
                    setPendingTickerCommand({
                        ticker_name,
                        pending: false,
                    }),
                );
            }
            if (axios.isAxiosError(e)) {
                if (e.response.data.run_at[0] && command === "SRT") {
                    notifyError(
                        `Ошибка перевода в real режим. ${e.response.data.run_at[0]}`,
                    );
                    return rejectWithValue(e.message);
                }

                notifyError(
                    `Ошибка при отправке команды ${command} для ${ticker_name}`,
                );
                return rejectWithValue(e.message);
            }
            throw e;
        }
    },
);
const checkAndResetMassCommandPending = (
    dispatch: any,
    getState: any,
    excludeQueueId: number,
) => {
    const state = getState();
    const { pendingCommands, massCommandPending } = state.logs;

    if (!massCommandPending) return;

    const remainingMassCommands = Object.values(pendingCommands).filter(
        (cmd: any) => cmd.is_mass && cmd.tcp_queue_id !== excludeQueueId,
    );

    if (remainingMassCommands.length === 0) {
        dispatch(setMassCommandPending(false));
    }
};
export const handleWebSocketCommandResult =
    (data: any) => (dispatch: any, getState: any) => {
        const {
            tcp_queue_id,
            status,
            command_type,
            ticker_name,
            error_message,
            user,
        } = data;

        if (status === "done" || status === "error") {
            dispatch(markCommandCompleted(tcp_queue_id));
            dispatch(removePendingCommand(tcp_queue_id));

            if (ticker_name) {
                const currentState = getState();
                Object.keys(currentState.logs.pendingCommands || {}).forEach(
                    (key) => {
                        const cmd = currentState.logs.pendingCommands[key];
                        if (cmd && cmd.ticker_name === ticker_name) {
                            dispatch(removePendingCommand(Number(key)));
                        }
                    },
                );

                dispatch(
                    setPendingTickerCommand({ ticker_name, pending: false }),
                );
            }
            // {
            //     "type": "command_result",
            //     "tcp_queue_id": 3198,
            //     "command_type": "RT",
            //     "status": "done",
            //     "ticker_name": "LAB1_BTCUSDC~DEP~BIN",
            //     "error_message": null,
            //     "action_log_id": null,
            //     "robot_id": "robot-1",
            //     "user": null
            // }

            if (status === "error") {
                if (command_type === "RT") {
                    return notifyError(
                        `Ошибка сохранения тикера ${ticker_name} ${error_message}`,
                    );
                }
            }
            if (status === "done") {
                if (command_type === "RT") {
                    return notifySuccess(
                        `Тикер ${ticker_name} успешно применён${user ? ` пользователем ${user}` : ""}`,
                    );
                }
                notifySuccess(
                    `Команда ${command_type} успешно выполнена${ticker_name ? ` для ${ticker_name}` : ""} ${user ? `пользователем ${user}` : ""}`,
                );
            } else {
                notifyError(
                    error_message ||
                        `Ошибка выполнения ${command_type} ${user ? `пользователем ${user}` : ""}`,
                );
            }

            checkAndResetMassCommandPending(dispatch, getState, tcp_queue_id);
        } else if (status === "timeout") {
            dispatch(markCommandCompleted(tcp_queue_id));

            dispatch(removePendingCommand(tcp_queue_id));
            if (ticker_name) {
                const currentState = getState();
                Object.keys(currentState.logs.pendingCommands || {}).forEach(
                    (key) => {
                        const cmd = currentState.logs.pendingCommands[key];
                        if (cmd && cmd.ticker_name === ticker_name) {
                            dispatch(removePendingCommand(Number(key)));
                        }
                    },
                );
                dispatch(
                    setPendingTickerCommand({ ticker_name, pending: false }),
                );
            }
            checkAndResetMassCommandPending(dispatch, getState, tcp_queue_id);
            if (status === "error") {
                if (command_type === "RT") {
                    return notifyError(
                        `Таймаут сохранения тикера ${ticker_name} ${error_message}`,
                    );
                }
            }
            notifyError(
                `Таймаут команды ${command_type} ${ticker_name ? `для тикера ${ticker_name}` : ""}  ${user ? `пользователем ${user}` : ""}`,
            );
        } else {
            console.warn(
                `[handleWebSocketCommandResult] Неизвестный статус: ${status}`,
            );
        }
    };

export const sendSectionCommand = createAsyncThunk(
    "post/sectionCommand",
    async (
        {
            section_id,
            command,
            additionalData,
        }: {
            section_id: number | string;
            command: string;
            additionalData?: object;
        },
        { rejectWithValue, dispatch, getState },
    ) => {
        try {
            const body: any = {
                section_id,
                command,
                ...additionalData,
            };

            const response = await instance.post(
                pathname + "/api/ticker/command",
                body,
            );

            let message = "";
            const tcpQueues = response.data.tcp_queues;
            const { logs } = getState() as RootState;
            if (tcpQueues && tcpQueues.length > 0) {
                tcpQueues.forEach((queue: any) => {
                    if (!logs.completedCommands[queue.tcp_queue_id]) {
                        dispatch(
                            setPendingCommand({
                                tcp_queue_id: queue.tcp_queue_id,
                                ticker_name: queue.ticker_name,
                                command_type: command,
                                is_mass: false,
                            }),
                        );
                    } else {
                        dispatch(removeCompletedCommand(queue.tcp_queue_id));
                    }
                });
            }
            switch (command) {
                case "RT":
                    message = `Перезагрузка без переподписки для всех тикеров в секции`;
                    break;
                case "WT":
                    message = `Все тикеры в секции переведены в бой`;
                    break;
                case "VT":
                    message = `Все тикеры в секции переведены в вирт`;
                    break;
                case "SCT":
                    message = `Все тикеры в секции переведены в закрытие`;
                    break;
                case "SAT":
                    message = `Выставлен АЗ для всех тикеров в секции`;
                    break;
                case "SRT":
                    message = `Все тикеры в секции запланированы в перевод в Real режим`;
                    break;
                case "CRT":
                    message = `Отменен перевод в Real режим для всех тикеров в секции`;
                    break;
                default:
                    message = `Команда ${command} успешно отправлена для секции`;
            }

            notifySuccess(message);
            return response.data;
        } catch (e: any) {
            if (axios.isAxiosError(e)) {
                if (e.response?.data?.run_at?.[0] && command === "SRT") {
                    notifyError(
                        `Ошибка перевода в real режим. ${e.response.data.run_at[0]}`,
                    );
                    return rejectWithValue(e.message);
                }

                notifyError(
                    `Ошибка при отправке команды ${command} для секции `,
                );
                return rejectWithValue(e.message);
            }
            throw e;
        }
    },
);

export const togglePinTab = createAsyncThunk<
    any,
    { id: number; is_pinned: boolean },
    { state: RootState }
>("toggle/tabPin", async ({ id, is_pinned }, { rejectWithValue, dispatch }) => {
    try {
        const response = await instance.patch(pathname + `/api/tab/${id}`, {
            is_pinned,
        });
        dispatch(getTabs());
        notifySuccess(is_pinned ? "Вкладка закреплена" : "Вкладка откреплена");
        return response.data;
    } catch (e) {
        if (axios.isAxiosError(e)) {
            const errorMsg =
                e.response?.data || "Ошибка при закреплении вкладки";
            notifyError(errorMsg);
            return rejectWithValue(e.message);
        }
    }
});

const tabsSlice = createSlice({
    name: "TabsSlice",
    initialState,
    reducers: {
        _updateWorkspaces(state, action) {
            const { data, reference } = action.payload;
            if (!Array.isArray(data) || !reference) {
                return;
            }
            const observable = Array.isArray(state.workspaces)
                ? current(state.workspaces)
                : [];
            const some_sections_empty =
                !Array.isArray(data) || data.some((x) => typeof x !== "object");
            const new_workspaces = [];
            if (some_sections_empty) {
                console.log("some_sections_empty", some_sections_empty);
            }
            for (const w of data) {
                if (
                    typeof w !== "object" ||
                    (typeof w === "object" && !Array.isArray(w.params))
                ) {
                    continue;
                }
                const existing = observable.find((ws) => ws.id === w.id);
                new_workspaces.push({ ...existing, ...w });
            }

            if (new_workspaces.length < observable.length) {
                for (const w of observable) {
                    if (!new_workspaces.find((x) => x.id === w.id)) {
                        new_workspaces.push(w);
                    }
                }
            }

            const has_diff = updateForWorkspacesHasDiff(data, observable);
            if (has_diff) {
                state.workspaces = new_workspaces;
            }
        },
        resetTabsState: () => {
            return initialState;
        },
        resetError(state) {
            state.error = null;
        },
        setTabs(state, action) {
            state.tabs = action.payload;
        },
        setCurrentTab(state, action) {
            state.currentTab = action.payload;
        },
        setActiveTab: (state, action: PayloadAction<number>) => {
            state.currentTab.id = action.payload;
        },
        resetCurrentTab(state) {
            state.currentTab = {} as ICurrentTab;
        },
        setPickedTickers(state, action) {
            if (typeof action.payload === "string") {
                const tickerName = action.payload;
                state.availableTickers = state.availableTickers.filter(
                    (ticker) => ticker !== tickerName,
                );
                state.pickedTickers.push(tickerName);
            } else {
                state.pickedTickers = action.payload;
                state.availableTickers = state.availableTickers.filter(
                    (ticker) => !state.pickedTickers.includes(ticker),
                );
            }
        },
        setGridPage(state, action) {
            state.currentGridPage = action.payload;
        },
        resetPickedTickers(state) {
            state.pickedTickers = [];
            state.availableTickers = state.tickers;
        },
        setTabOption(state, action) {
            const { number, optionId } = action.payload;
            state.tabsNumber = number;
            state.optionId = optionId;
            // state.layoutStyle = OptionStyleSelect(optionId)
        },

        removeTickerFromAddModal(state, action) {
            state.addTickerPopup = state.addTickerPopup.filter(
                (ticker) => ticker !== action.payload,
            );
        },
        setWorkspaceToUpdate(state, action) {
            state.workspaceToUpdateId = action.payload;
        },
        resetWorkspaceToUpdate(state) {
            state.workspaceToUpdateId = null;
        },
        setStatuses(state, action: PayloadAction<TickerStatus[]>) {
            const newStatuses = action.payload.reduce(
                (acc, item) => ({
                    ...acc,
                    [item.ticker_name]: item,
                }),
                {} as { [ticker_name: string]: TickerStatus },
            );
            state.tickerStatuses = newStatuses;
        },
        updateStatus(state, action: PayloadAction<TickerStatus>) {
            state.tickerStatuses[action.payload.ticker_name] = action.payload;
        },
        initSectionStatuses(state, action) {
            const statuses = {};

            for (const item of action.payload) {
                statuses[item] = false;
            }

            state.sectionStatuses = statuses;
        },
        setSrtSChedule(state, action) {
            state.srt_schedule = action.payload;
        },
        initSectionStatusesWithValues(state, action) {
            const statuses = {};

            for (const item of action.payload) statuses[item.id] = item.value;

            state.sectionStatuses = statuses;
        },
        toggleSectionStatus(state, action) {
            state.sectionStatuses[action.payload] =
                !state.sectionStatuses[action.payload];
        },
        changeSectionStatus(state, action) {
            state.sectionStatuses[action.payload.id] = action.payload.value;
        },
        changeSectionsNameColumnPinStatus(state, action) {
            state.sectionsNameColumnPinStatus[action.payload.id] =
                action.payload.value;
        },
        setSectionName(state, action) {
            if (state.currentTab && state.currentTab.sections)
                if (
                    state.currentTab.sections.findIndex(
                        (section) => section.id === action.payload.id,
                    ) != -1
                )
                    state.currentTab.sections[
                        state.currentTab.sections.findIndex(
                            (section) => section.id === action.payload.id,
                        )
                    ].name = action.payload.name;
        },
        setTableSettings(state, action) {
            const { id, fontScale, isAutoSizing, row_density } = action.payload;

            const existingIndex = state.tables_sizes.findIndex(
                (setting) => setting.id === id,
            );

            state.tables_sizes[existingIndex] = {
                id,

                fontScale,
                row_density,
                isAutoSizing,
            };

            if (existingIndex === -1) {
                state.tables_sizes.push({
                    id,

                    fontScale,
                    row_density,
                    isAutoSizing,
                });
            }
        },

        resetTabsStateForRobot: (state) => {
            state.tabs = [];
            state.workspaces = [];
            state.error = null;
            state.loading = false;
        },
        setPendingTickerCommand: (
            state,
            action: PayloadAction<{ ticker_name: string; pending: boolean }>,
        ) => {
            state.pendingTickerCommands[action.payload.ticker_name] =
                action.payload.pending;
        },

        setUpdateTickersError(state, action: PayloadAction<string | null>) {
            state.errorUpdateTickers = action.payload;
        },
        clearUpdateTickersError(state) {
            state.errorUpdateTickers = null;
        },
    },
    extraReducers: {
        [getTabs.fulfilled.type]: (state, action) => {
            // console.log(action.payload)
            state.tabs = action.payload;
            state.loading = false;
        },
        [getTabs.pending.type]: (state) => {
            state.loading = true;
        },

        [getTabs.rejected.type]: (state, action) => {
            state.loading = false;
            state.error = action.payload;
        },
        [getTabById.fulfilled.type]: (state, action) => {
            state.currentTab = action.payload;
            state.loading = false;
        },
        [getTabById.pending.type]: (state) => {
            state.loading = true;
        },
        [getTabById.rejected.type]: (state, action) => {
            state.loading = false;
            state.error = action.payload;
        },
        // [renameTab.fulfilled.type]: (state) => {
        //     state.error = null
        //     state.loading = false
        // },
        // [renameTab.pending.type]: (state) => {
        //     state.loading = true
        // },
        // [renameTab.rejected.type]: (state, action) => {
        //     state.loading = false
        //     state.error = action.payload
        // },

        // POINT

        [getAreasByTabId.fulfilled.type]: (state, action) => {
            const updated = action.payload;
            if (!Array.isArray(updated)) {
                return;
            }
            state.workspaces = updated.map((update) => {
                const existing = state.workspaces.find(
                    (ws) => ws.id === update.id,
                );
                return {
                    ...existing,
                    ...update,
                    all_columns:
                        update.all_columns || existing?.all_columns || [],
                };
            });
            state.loading = false;
            state.error = null;
        },
        [getAreasByTabId.pending.type]: (state) => {},
        [getAreasByTabId.rejected.type]: (state, action) => {
            state.error = action.payload;
        },

        [getTickers.fulfilled.type]: (state, action) => {
            try {
                state.tickers = state.availableTickers = action.payload;
                state.addTickerPopup = action.payload.map(
                    (ticker: string) => ticker,
                );
                state.loading = false;
            } catch (e) {
                // console.error("cannot process tickers")
                state.loading = false;
            }
        },
        [getTickers.pending.type]: (state) => {
            state.loading = true;
        },
        [getTickers.rejected.type]: (state, action) => {
            state.loading = false;
            state.error = action.payload;
        },
        [addNewWorkspace.pending.type]: (state) => {
            state.loading = true;
        },
        [addNewWorkspace.rejected.type]: (state, action) => {
            state.loading = false;
            state.error = action.paylaod;
        },
        [addNewWorkspace.fulfilled.type]: (state) => {
            state.pickedTickers = [];
            state.loading = false;
            state.error = null;
        },
        [updateWorkspaceWidths.pending.type]: (state) => {
            state.loading = true;
            state.error = null;
        },
        [updateWorkspaceWidths.fulfilled.type]: (state, action) => {
            state.loading = false;
            state.error = null;
            if (action.payload && state.currentTab.sections) {
                state.currentTab.sections = state.currentTab.sections.map(
                    (section) =>
                        section.id === action.meta.arg.id
                            ? {
                                  ...section,
                                  columns_widths:
                                      action.meta.arg.columns_widths,
                              }
                            : section,
                );
            }
        },
        [updateWorkspaceWidths.rejected.type]: (state, action) => {
            state.loading = false;
            state.error = action.payload || "Failed to update column widths";
        },
        [updateAreaPickedTickers.pending.type]: (state) => {
            state.errorUpdateTickers = null;
        },

        [updateAreaPickedTickers.fulfilled.type]: (state) => {
            state.errorUpdateTickers = null;
        },

        [updateAreaPickedTickers.rejected.type]: (state, action) => {
            state.errorUpdateTickers =
                action.payload || "Неизвестная ошибка при обновлении тикеров";
        },
    },
});

export const {
    resetTabsState,
    resetError,
    setPickedTickers,
    resetPickedTickers,
    removeTickerFromAddModal,
    setWorkspaceToUpdate,
    resetWorkspaceToUpdate,
    _updateWorkspaces,
    setStatuses,
    updateStatus,
    initSectionStatuses,
    toggleSectionStatus,
    setSectionName,
    changeSectionStatus,
    setGridPage,
    initSectionStatusesWithValues,
    setSrtSChedule,
    setTableSettings,
    resetTabsStateForRobot,
    changeSectionsNameColumnPinStatus,
    setPendingTickerCommand,
    setUpdateTickersError,
    clearUpdateTickersError,
} = tabsSlice.actions;

export default tabsSlice.reducer;
